// i18n
import { useState } from 'react';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import {
  spacing,
  fontSize,
  zIndex,
  mediaMax,
} from 'presentations/views/styles';
import { gray03, gray04 } from 'presentations/views/styles/color';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import TextField from 'presentations/views/components/atoms/TextField';
import { ErrorList } from 'domains/models';
import TextArea from 'presentations/views/components/atoms/TextArea';

interface Props {
  onSubmit: Function;
  onChange: Function;
  onBlur?: Function;
  onClose?: Function;
  email: string | null;
  name: string | null;
  message: string | null;
  company: string | null;
  isLoading: boolean;
  isCompanyHidden?: boolean;
  isCompanyRequired?: boolean;
  errors: ErrorList;
  color?: string;
  isLightColor?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const onSubmit = async () => {
    const result = await props.onSubmit();
    if (result) {
      setIsSubmit(true);
    }
  };

  return (
    <Dialog
      isOpen
      titleComponent={
        <TitleContainer>
          {!isSubmit ? t('title') : t('complete')}
          <CloseIconButton aria-label="close" onClick={props.onClose}>
            <CloseIcon name="close" size={fontSize(20)} />
          </CloseIconButton>
        </TitleContainer>
      }
      actions={
        <ActionContainer>
          <DialogSubmitField
            onSubmit={!isSubmit ? onSubmit : undefined}
            onCancel={props.onClose}
            submitLabel={t('submit')}
            cancelLabel={isSubmit ? t('close') : t('close')}
            color={props.color}
            isLoading={props.isLoading}
            isLightColor={props.isLightColor}
          />
        </ActionContainer>
      }
      onClose={props.onClose}
      scroll
      maxHeight="90vh"
      maxWidth={600}
      mobileMaxHeight="100%"
      zIndex={zIndex(1500)}
    >
      {!isSubmit ? (
        <Form>
          <Block>
            <TextField
              isRequired
              isFullWidth
              label={t('Name')}
              name="name"
              value={props.name}
              placeholder={t('Name placeholder')}
              errors={props.errors['name']}
              onChange={props.onChange}
              onBlur={props.onBlur}
            />
          </Block>
          <Block>
            <TextField
              isRequired
              isFullWidth
              label={t('Email')}
              name="email"
              value={props.email}
              placeholder={t('Email placeholder')}
              onChange={props.onChange}
              onBlur={props.onBlur}
              errors={props.errors['email']}
            />
          </Block>
          {!props.isCompanyHidden && (
            <Block>
              <TextField
                isRequired={!!props.isCompanyRequired}
                isNotRequired={!props.isCompanyRequired}
                isFullWidth
                label={t('Company')}
                name="company"
                value={props.company}
                placeholder={t('Company placeholder')}
                onChange={props.onChange}
                onBlur={props.onBlur}
                errors={props.errors['company']}
              />
            </Block>
          )}
          <Block>
            <TextArea
              isRequired
              isFullWidth={true}
              label={t('Message')}
              name="message"
              value={props.message}
              placeholder={t('Message placeholder')}
              onChange={props.onChange}
              errors={props.errors['message']}
            />
          </Block>
        </Form>
      ) : (
        <Complete>{t('complete desc')}</Complete>
      )}
    </Dialog>
  );
}

const TitleContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: ${spacing(16)};
  border-bottom: 1px solid ${gray04};
  line-height: 44px;
  vertical-align: middle;
`;
const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
  color: ${gray03};
`;
const CloseIcon = styled(Icon)``;

const ActionContainer = styled.div`
  padding-top: ${spacing(24)};
  border-top: 1px solid ${gray04};
`;

const Form = styled.div`
  display: block;
  max-height: 70vh;
  width: 100%;
  position: relative;
  padding: ${spacing(8)} ${spacing(40)} 0;
  ${mediaMax.smartphone} {
    height: inherit;
    padding: ${spacing(8)} ${spacing(16)} 0;
  }
`;

const Block = styled.div`
  margin-bottom: ${spacing(40)};
  :last-of-type {
    /* margin-bottom: 0; */
  }
`;

const Complete = styled.div`
  display: block;
  max-height: 70vh;
  width: 100%;
  position: relative;
  // padding: 0 ${spacing(40)} 0;
  white-space: pre-wrap;
  ${mediaMax.smartphone} {
    height: inherit;
    padding: ${spacing(8)} ${spacing(16)} 0;
  }
`;
